import {
  CUSTOM_SITE, SIDE_LEFT, SIDE_RIGHT, THERMOGRAPHY_REGIONS, formatTemp, isTempInRange, parseTemp, deltaOf,
} from '../../pages/appointment/modules/thermography/thermography';

/**
 * Estado do formulário da termografia: valores digitados por campo
 * (`site|side`), locais livres e a conversão de/para as leituras gravadas.
 */

export const fieldKey = (site, side) => `${site}|${side || ''}`;

let customSeq = 0;
export const newCustomRow = (patch = {}) => {
  customSeq += 1;
  return { uid: `c${customSeq}`, label: '', bilateral: false, value: '', right: '', left: '', ...patch };
};

/** Leituras gravadas para { values, customs }. */
export function readingsToForm(readings) {
  const values = {};
  const customs = new Map();
  (Array.isArray(readings) ? readings : []).forEach((reading) => {
    if (!reading || reading.value == null) return;
    const text = formatTemp(reading.value);
    if (reading.site !== CUSTOM_SITE) {
      values[fieldKey(reading.site, reading.side)] = text;
      return;
    }
    const id = String(reading.label || '').trim().toLowerCase();
    const row = customs.get(id) || newCustomRow({ label: String(reading.label || '').trim() });
    if (reading.side === SIDE_RIGHT) { row.bilateral = true; row.right = text; }
    else if (reading.side === SIDE_LEFT) { row.bilateral = true; row.left = text; }
    else row.value = text;
    customs.set(id, row);
  });
  return { values, customs: [...customs.values()] };
}

/** Regiões que têm algum valor preenchido em `values`. */
export function regionsWithValues(values) {
  const keys = new Set();
  THERMOGRAPHY_REGIONS.forEach((region) => region.sites.forEach((site) => {
    const sides = site.bilateral ? [SIDE_RIGHT, SIDE_LEFT] : [null];
    if (sides.some((side) => values[fieldKey(site.key, side)])) keys.add(region.key);
  }));
  return keys;
}

/** Texto digitado que não é temperatura válida na faixa. */
export const isInvalidTemp = (text) => String(text || '').trim() !== '' && !isTempInRange(parseTemp(text));

/** Delta T ao vivo entre dois textos, ou null se algum lado não for válido. */
export function liveDelta(rightText, leftText) {
  const right = parseTemp(rightText);
  const left = parseTemp(leftText);
  if (!isTempInRange(right) || !isTempInRange(left)) return null;
  return deltaOf(right, left);
}

/**
 * Monta as leituras para gravar. Devolve { readings } ou { error } com a
 * mensagem para o usuário.
 */
export function formToReadings(values, customs) {
  const readings = [];
  let invalid = false;

  const push = (site, side, label, text) => {
    if (String(text || '').trim() === '') return;
    if (isInvalidTemp(text)) { invalid = true; return; }
    readings.push({ site, side, label, value: parseTemp(text) });
  };

  THERMOGRAPHY_REGIONS.forEach((region) => region.sites.forEach((site) => {
    if (site.bilateral) {
      push(site.key, SIDE_RIGHT, null, values[fieldKey(site.key, SIDE_RIGHT)]);
      push(site.key, SIDE_LEFT, null, values[fieldKey(site.key, SIDE_LEFT)]);
    } else {
      push(site.key, null, null, values[fieldKey(site.key, null)]);
    }
  }));

  const labels = new Set();
  for (const row of customs) {
    const label = row.label.trim();
    const texts = row.bilateral ? [row.right, row.left] : [row.value];
    const filled = texts.some((text) => String(text || '').trim() !== '');
    if (!filled) continue;
    if (label === '') return { error: 'Dê um nome a cada local livre preenchido.' };
    const id = label.toLowerCase();
    if (labels.has(id)) return { error: `O local "${label}" aparece mais de uma vez.` };
    labels.add(id);
    if (row.bilateral) {
      push(CUSTOM_SITE, SIDE_RIGHT, label, row.right);
      push(CUSTOM_SITE, SIDE_LEFT, label, row.left);
    } else {
      push(CUSTOM_SITE, null, label, row.value);
    }
  }

  if (invalid) return { error: 'Cada temperatura deve ficar entre 20,0 e 42,0 °C.' };
  if (readings.length === 0) return { error: 'Registre a temperatura de pelo menos um local.' };
  return { readings };
}
